import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { UploadCloud } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useFetchProjectsQuery, useGetProjectQuery } from '../../services/projectsApi'
import { useGetTagsQuery } from '../../services/tagApi'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { Project } from '../../types/models/project'
// no prefetch needed; images are fetched on-demand in resolver
import { projectsApi } from '../../services/projectsApi'
import { apiTags } from '../../services/apiTags'
import { contentApi } from '../../services/contentApi'
import { publishExportProgress } from '../../utils/exportProgressBus'
import { useNavigate } from 'react-router-dom'
import {
  clearFilterTags,
  setActiveDatasetSubView,
  setActiveInferenceSubView,
  setActiveModelSubView,
  setSelectedProjectIdAndClearPrediction,
} from '../../store/slices/projectSlice'
import {
  setDatasetSources,
  setTrainingSource,
  setValidationSource,
} from '../../store/slices/datasetSlice'
import { clearSelectedModelId } from '../../store/slices/modelSlice'
import { setAnnotatedPage, setUnannotatedPage } from '../../store/slices/annotationSlice'
import { useExportSnippets } from '../../hooks/useExportSnippets'
import TargetProjectSelector from './ExportSnippetsModal/TargetProjectSelector'
import ExportOptionsPanel from './ExportSnippetsModal/ExportOptionsPanel'

// Use Bootstrap classes instead of styled-components for theming
const DarkModal: React.FC<React.ComponentProps<typeof Modal>> = (props) => (
  <Modal {...props} contentClassName="bg-dark text-light border-0" dialogClassName="" />
)

interface ExportSnippetsModalProps {
  show: boolean
  onHide: () => void
  // Back-compat for single-image use
  contentId?: string
  boxes?: AnnotationDataBoundingBox[]
  // New multi-image bulk support
  items?: Array<{ contentId: string; boxes: AnnotationDataBoundingBox[] }>
}

const ExportSnippetsModal: React.FC<ExportSnippetsModalProps> = ({
  show,
  onHide,
  contentId,
  boxes,
  items,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const sourceProjectId = useAppSelector(selectSelectedProjectId)
  const { data: sourceProject } = useGetProjectQuery(sourceProjectId || '', {
    skip: !sourceProjectId,
  })

  const { data: tagsResp } = useGetTagsQuery(
    {
      project_id: sourceProjectId || '',
      dataset_id: sourceProject?.datasetid || '',
    },
    { skip: !sourceProjectId || !sourceProject?.datasetid }
  )

  // Fetch projects to select an existing classification project
  const { data: projectsResp } = useFetchProjectsQuery({
    page: 0,
    limit: 200,
  })
  const classificationProjects: Project[] = useMemo(
    () =>
      (projectsResp?.projects || []).filter((p: Project) => p.annotation_type === 'classification'),
    [projectsResp]
  )

  const exportHook = useExportSnippets()

  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [targetProjectId, setTargetProjectId] = useState<string>('')
  const [newProjectName, setNewProjectName] = useState<string>('')
  const [imageSize, setImageSize] = useState<number>(512)
  const [error, setError] = useState<string>('')
  const [uploadAsUnannotated, setUploadAsUnannotated] = useState<boolean>(false)
  const [datasetSplit, setDatasetSplit] = useState<'training' | 'validation'>('training')
  const isOBBProject = sourceProject?.annotation_type === 'oriented_bounding_box'
  const [rotateLongSideVertical, setRotateLongSideVertical] = useState<boolean>(false)

  // Load the source image as a local object URL (blob) so canvas uses a same-origin URL
  const singleMode = !!contentId && !!boxes && (!items || items.length === 0)

  // Set default target project once projects load and no selection yet
  useEffect(() => {
    if (show && !targetProjectId && classificationProjects.length > 0) {
      // Prefer previously used project if available and valid
      const stored = localStorage.getItem('lastExportProjectId') || ''
      const validStored = stored && classificationProjects.some((p) => p.id === stored)
      setTargetProjectId(validStored ? stored : classificationProjects[0].id)
    }
  }, [show, classificationProjects, targetProjectId])

  // Persist last selected export project
  useEffect(() => {
    if (targetProjectId) {
      try {
        localStorage.setItem('lastExportProjectId', targetProjectId)
      } catch {}
    }
  }, [targetProjectId])

  const onExport = async () => {
    try {
      setError('')
      console.log('[ExportSnippetsModal] start export clicked')
      if (!sourceProjectId || !sourceProject?.datasetid) {
        throw new Error('Source project not ready')
      }
      const exportItems =
        items && items.length > 0 ? items : contentId && boxes ? [{ contentId, boxes }] : []
      console.log('[ExportSnippetsModal] prepared exportItems', {
        items: exportItems.length,
        totalBoxes: exportItems.reduce((acc, it) => acc + it.boxes.length, 0),
      })
      // For single image exports, open the toast expanded initially
      if (singleMode) {
        try {
          publishExportProgress({ visible: true, minimized: false })
        } catch {}
      }
      // Fire-and-forget: start export and immediately close the modal
      void exportHook.startExport({
        mode,
        targetProjectId,
        newProjectName,
        datasetSplit,
        uploadAsUnannotated,
        imageSize,
        rotateLongSideVertical: isOBBProject ? rotateLongSideVertical : false,
        items: exportItems,
        resolveImageObjectUrl: async (cid: string) => {
          console.time(`[resolveImageObjectUrl] ${cid}`)
          const res = await dispatch(
            contentApi.endpoints.getContentImage.initiate({
              ContentID: cid,
              project_id: sourceProjectId,
              dataset_id: sourceProject?.datasetid,
            })
          )
          console.timeEnd(`[resolveImageObjectUrl] ${cid}`)
          if ('data' in res && typeof res.data === 'string') return res.data
          throw new Error('Failed to load image')
        },
        classificationProjects,
        tags: tagsResp?.tags,
        sourceProjectId: sourceProjectId,
      })
      // Close immediately; progress continues in the toast
      onHide()
    } catch (error: unknown) {
      console.error(error)
      const message =
        typeof error === 'object' && error && 'message' in error
          ? String((error as { message: unknown }).message)
          : 'Export failed'
      setError(message)
    }
  }

  const totalSelected =
    items && items.length > 0
      ? items.reduce((acc, it) => acc + it.boxes.length, 0)
      : boxes
      ? boxes.length
      : 0
  const canExport = useMemo(() => {
    const hasSelection = totalSelected > 0
    if (mode === 'new') return newProjectName.trim().length > 0 && hasSelection
    return !!targetProjectId && hasSelection
  }, [mode, newProjectName, targetProjectId, totalSelected])

  const canStart =
    canExport &&
    (exportHook.status === 'idle' ||
      exportHook.status === 'error' ||
      exportHook.status === 'success')

  useEffect(() => {
    try {
      console.log('[ExportSnippetsModal] state', {
        totalSelected,
        mode,
        targetProjectId,
        hasTarget: !!targetProjectId,
        newProjectName,
        canExport,
        canStart,
        status: exportHook.status,
      })
    } catch {}
  }, [totalSelected, mode, targetProjectId, newProjectName, canExport, canStart, exportHook.status])

  return (
    <DarkModal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <UploadCloud size={18} /> Export snippets to classification project
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          {error && (
            <Alert variant="danger" className="mb-0">
              {error}
            </Alert>
          )}
          <Row>
            <Col md={6}>
              <TargetProjectSelector
                mode={mode}
                setMode={setMode}
                classificationProjects={classificationProjects}
                targetProjectId={targetProjectId}
                setTargetProjectId={setTargetProjectId}
                newProjectName={newProjectName}
                setNewProjectName={setNewProjectName}
              />
            </Col>
            <Col md={6}>
              <ExportOptionsPanel
                datasetSplit={datasetSplit}
                setDatasetSplit={setDatasetSplit}
                uploadAsUnannotated={uploadAsUnannotated}
                setUploadAsUnannotated={setUploadAsUnannotated}
                imageSize={imageSize}
                setImageSize={setImageSize}
                selectedCount={totalSelected}
                isOBBProject={isOBBProject}
                rotateLongSideVertical={rotateLongSideVertical}
                setRotateLongSideVertical={setRotateLongSideVertical}
              />
            </Col>
          </Row>

          {/* No prefetch; nothing to prepare until Start is pressed */}

          {(exportHook.status === 'creating' ||
            exportHook.status === 'preparing' ||
            exportHook.status === 'uploading') && (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span className="text-secondary">
                {exportHook.status === 'creating' && 'Creating project...'}
                {exportHook.status === 'preparing' && 'Preparing images and labels...'}
                {exportHook.status === 'uploading' && 'Uploading...'}
              </span>
            </div>
          )}

          {exportHook.status === 'success' && (
            <Alert
              variant="success"
              className="mb-0 d-flex justify-content-between align-items-center"
            >
              <span>
                Export complete: {exportHook.resultCount} image
                {exportHook.resultCount !== 1 ? 's' : ''} uploaded.
              </span>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  const pid = exportHook.finalTargetProjectId
                  if (pid && pid.length > 0) {
                    try {
                      // Clear navigation-related state to mirror ProjectCard behavior
                      localStorage.removeItem('activeTab')
                      localStorage.removeItem('selectedModelTab')
                      localStorage.removeItem('activeDatasetSubView')
                      localStorage.removeItem('activeInferenceSubView')
                      localStorage.removeItem('activeModelSubView')
                      localStorage.removeItem('inferenceLoop_currentPhase')
                      localStorage.removeItem('compareModelId')

                      dispatch(setTrainingSource([]))
                      dispatch(setValidationSource([]))
                      dispatch(setDatasetSources([]))
                      dispatch(clearFilterTags())
                      dispatch(setSelectedProjectIdAndClearPrediction(pid))
                      dispatch(clearSelectedModelId(pid))
                      dispatch(setActiveInferenceSubView('Overview'))
                      dispatch(setActiveDatasetSubView('Overview'))
                      dispatch(setActiveModelSubView('Overview'))
                      // Reset pages in DatasetLabelingView
                      dispatch(setAnnotatedPage(1))
                      dispatch(setUnannotatedPage(1))
                    } catch {}
                    navigate(`/overview/${pid}/overview`)
                    setTimeout(() => {
                      try {
                        dispatch(projectsApi.util.invalidateTags([apiTags.Project]))
                      } catch {}
                    }, 1000)
                  } else {
                    navigate('/projects')
                  }
                }}
              >
                {`Go to ${exportHook.finalTargetProjectName || 'project'}`}
              </Button>
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-danger"
          onClick={onHide}
          disabled={
            exportHook.status === 'creating' ||
            exportHook.status === 'preparing' ||
            exportHook.status === 'uploading'
          }
        >
          Close
        </Button>
        <Button
          variant="outline-primary"
          onClick={onExport}
          disabled={!canStart}
          title={exportHook.status === 'idle' ? undefined : 'Export in progress'}
        >
          Start export
        </Button>
      </Modal.Footer>
    </DarkModal>
  )
}

export default ExportSnippetsModal
